import { useEffect, useRef, useState } from 'react';
import { getGeocode, getLatLng, getZipCode } from 'use-places-autocomplete';

import { AddressFieldType } from '@restworld/data-services';

import { useGmapsScript } from './use-gmaps-script';
import { trackPlacesUsage } from '../observability/track-places-usage';

export const useAddressAdditionalFields = ({
  addressString,
  initialFields,
  // Call-site id for Google Geocoding usage telemetry (OpenObserve).
  surface = 'unspecified',
}: {
  addressString: string;
  /**
   * Geo fields already persisted for the current address (e.g. the saved form value used
   * to hydrate the input on mount). When `addressString` matches `initialFields.address`
   * and coordinates are present, they are returned as-is instead of re-running a billed
   * Geocoding call for data we already have.
   */
  initialFields?: AddressFieldType;
  surface?: string;
}) => {
  const [data, setData] = useState<AddressFieldType>({});
  const isScriptReady = useGmapsScript({ libraries: 'places' });
  // Captured once on mount: identifies the hydration value; later user selections
  // always differ from it and geocode normally.
  const initialFieldsRef = useRef(initialFields);

  useEffect(() => {
    if (!addressString) return;

    const known = initialFieldsRef.current;
    if (known?.address === addressString && known.address_lat != null && known.address_lon != null) {
      setData(known);
      return;
    }

    if (!isScriptReady) return;
    getLocationAddressFields(addressString, undefined, surface)
      .then((data) => setData(data as AddressFieldType))
      .catch((e) => e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressString, isScriptReady]);

  return data;
};
export const getLocationAddressFields = async (addressString?: string, placeId?: string, surface = 'unspecified') => {
  trackPlacesUsage({ surface, api: 'geocoding' });
  // Prefer the exact place over free-text geocoding when the caller already has a
  // place_id from the autocomplete prediction: same billing, zero ambiguity for
  // similarly-named venues.
  const data = await getGeocode(placeId ? { placeId } : { address: addressString });
  const addressComponents: { types: string[]; long_name: string }[] = data[0].address_components;
  const getLnNameValue = (
    key: 'country' | 'locality' | 'administrative_area_level_1' | 'administrative_area_level_2' | 'street_number'
  ) => addressComponents.find((c) => c.types.includes(key))?.long_name ?? '';
  const country = getLnNameValue('country');
  const locality = getLnNameValue('locality');
  const administrativeAreaLevelOne = getLnNameValue('administrative_area_level_1');
  const administrativeAreaLevelTwo = getLnNameValue('administrative_area_level_2');
  const street_number = getLnNameValue('street_number');
  const postalCode = getZipCode(data[0], false);
  const { lat, lng } = getLatLng(data[0]);
  const addressData = {
    address: addressString ?? data[0].formatted_address,
    address_lat: lat,
    address_lon: lng,
    locality: locality,
    country: country,
    postal_code: postalCode,
    administrative_area_level_1: administrativeAreaLevelOne,
    administrative_area_level_2: administrativeAreaLevelTwo,
    street_number,
  };
  return addressData;
};
